import { ActionLogToken } from "features/actionLogManagement/di/ActionLogToken";
import { AirtimeManagementToken } from "features/airTimeManagement/di/AirtimeManagementToken";
import { AirTimeRegistrationToken } from "features/airTimeRegistration/di/AirTimeRegistrationToken";
import { ApiDocumentToken } from "features/apiDocumentFeature/di/ApiDocumentToken";
import { AppFeedbackToken } from "features/appFeedback/di/AppFeedbackToken";
import { AppProfileToken } from "features/appProfileRegister/di/AppProfileToken";
import { B2CUserToken } from "features/b2cUserManagement/di/B2CUserToken";
import { BalanceHistoryToken } from "features/balanceHistory/di/BalanceHistoryToken";
import { CapabilityToken } from "features/capabilityManagement/di/CapabilityToken";
import { CashFlowToken } from "features/cashFlow/di/CashFlowToken";
import { CdnConfigToken } from "features/cdnConfiguration/di/CdnConfigToken";
import { CountryConfigToken } from "features/countryConfiguration/di/CountryConfigToken";
import { DiscountManagementToken } from "features/discountManagement/di/DiscountManagementToken";
import { ErrorCodeGeneralToken } from "features/errorCodeGeneralFeature/di/ErrorCodeGeneralToken";
import { IndividualUserToken } from "features/individualUsersManagement/di/IndividualUserToken";
import { IOTSimListLookupToken } from "features/iotSimListLookup/di/IOTSimListLookupToken";
import { MobiSimRegistrationToken } from "features/mobiSimRegistration/di/MobiSimRegistrationToken";
import { NiceSimRegistrationToken } from "features/niceSimRegistration/di/NiceSimRegistrationToken";
import Notfound from "features/notFound/NotFound";
import { OfficialDispatchToken } from "features/officialDispatch/di/OfficialDispatchToken";
import { PackageLookupToken } from "features/packageLookup/di/PackageLookupToken";
import { PackageRegistrationToken } from "features/packageRegistration/di/PackageRegistrationToken";
import { PermissionDataToken } from "features/permissionDataManagement/di/PermissionDataToken";
import { RoleManagmentToken } from "features/roleManagement/di/RoleManagementToken";
import { SimLookupToken } from "features/simStatusLookup/di/SimLookupToken";
import { StoreToken } from "features/storeManagement/di/StoreToken";
import { SupplierManagementToken } from "features/supplierManagement/di/SupplierManagementToken";
import { SystemUserToken } from "features/systemUserManagement/di/SystemUserToken";
import { TravelSimRegistrationToken } from "features/travelSimRegistration/di/TravelSimRegistrationToken";
import { UserPermissionToken } from "features/userPermissionManagement/di/UserPermissionToken";
import { VnskySupportAlertToken } from "features/vnskySupportAlert/di/VnskySupportAlertToken";
import { VnskySupportOcsInfoToken } from "features/vnskySupportOcsInfo/di/VnskySupportOcsInfoToken";
import { ComponentType, FC, lazy, LazyExoticComponent } from "react";
import {
  MobileDataCategories,
  MobileDataService,
} from "utils/enums/MobileDataEnums";
import { TypeOfSupportEnum } from "utils/enums/TypeOfSupportEnum";
import { TypeOfTravelSimEnum } from "utils/enums/TypeOfTravelSimEnum";
import { InjectionToken } from "utils/injectionToken/InjectionToken";

export interface IAppPages {
  Component: LazyExoticComponent<FC<any> | ComponentType>;
  token?: symbol | string;
  addtionalProps?: unknown;
}

class AppPages {
  static PageKeys: Record<string, IAppPages> = {
    GeneralFeature: {
      Component: lazy(
        () => import("features/generals/presentation/views/GeneralFeature"),
      ),
    },
    ErrorCodeGeneralFeature: {
      Component: lazy(
        () =>
          import("features/errorCodeGeneralFeature/presentation/views/ErrorCodeGeneralFeature"),
      ),
      token: ErrorCodeGeneralToken.ErrorCodeGeneralViewModel,
    },
    AppSimMobifoneFeature: {
      Component: lazy(
        () =>
          import("features/mobiSimRegistration/presentation/views/MobiSimRegistrationFeature"),
      ),
      token: MobiSimRegistrationToken.MobiSimRegistrationViewModel,
    },
    AirTimeManagementFeature: {
      Component: lazy(
        () =>
          import("features/airTimeManagement/presentation/views/AirTimeManagementFeature"),
      ),
      token: AirtimeManagementToken.AirTimeManagementViewModel,
    },
    AirTimeRegistrationFeature: {
      Component: lazy(
        () =>
          import("features/airTimeRegistration/presentation/views/AirTimeRegistrationFeature"),
      ),
      token: AirTimeRegistrationToken.AirTimeRegistrationViewModel,
    },
    AppDataRegisterFeature: {
      Component: lazy(
        () =>
          import("features/packageRegistration/presentation/views/PackageRegistrationFeature"),
      ),
      token: PackageRegistrationToken.PackageRegistrationViewModel,
    },
    AppNiceSimFeature: {
      Component: lazy(
        () =>
          import("features/niceSimRegistration/presentation/views/NiceSimRegistrationFeature"),
      ),
      token: NiceSimRegistrationToken.NiceSimRegistrationViewModel,
    },
    BalanceHistoryFeature: {
      Component: lazy(
        () =>
          import("features/balanceHistory/presentation/view/BalanceHistoryFeature"),
      ),
      token: BalanceHistoryToken.BalanceHistoryViewModel,
    },
    InboundTravelSimFeature: {
      Component: lazy(
        () =>
          import("features/travelSimRegistration/presentation/views/TravelSimRegistrationFeature"),
      ),
      token: TravelSimRegistrationToken.TravelSimRegistrationViewModel,
      addtionalProps: { travelType: TypeOfTravelSimEnum.inbound },
    },
    OutboundTravelSimFeature: {
      Component: lazy(
        () =>
          import("features/travelSimRegistration/presentation/views/OutboundRegistrationFeature"),
      ),
      token: TravelSimRegistrationToken.OutboundRegistrationViewModel,
      addtionalProps: { travelType: TypeOfTravelSimEnum.outbound },
    },
    CashFlowFeature: {
      Component: lazy(
        () => import("features/cashFlow/presentation/views/CashFlowFeature"),
      ),
      token: CashFlowToken.CashFlowViewModel,
    },
    PackageLookupFeature: {
      Component: lazy(
        () =>
          import("features/packageLookup/presentation/view/PackageLookupFeature"),
      ),
      token: PackageLookupToken.PackageLookupViewModel,
    },
    SimLookupFeature: {
      Component: lazy(
        () =>
          import("features/simStatusLookup/presentation/view/SimLookupFeature"),
      ),
      token: SimLookupToken.SimLookupViewModel,
    },
    ApiDocManagementFeature: {
      Component: lazy(
        () =>
          import("features/apiDocumentFeature/presentation/view/ApiDocumentFeature"),
      ),
      token: ApiDocumentToken.ApiDocumentViewModel,
      addtionalProps: TypeOfSupportEnum.apiDoc,
    },
    DispatchManagementFeature: {
      Component: lazy(
        () =>
          import("features/officialDispatch/presentation/view/OfficialDispatchFeature"),
      ),
      token: OfficialDispatchToken.OfficialDispatchViewModel,
      addtionalProps: TypeOfSupportEnum.officialDispatch,
    },
    VnskySupportOcsInfoFeature: {
      Component: lazy(
        () =>
          import("features/vnskySupportOcsInfo/presentation/view/VnskySupportOcsInfoFeature"),
      ),
      token: VnskySupportOcsInfoToken.VnskySupportOcsInfoViewModel,
    },
    VnskySupportAlertFeature: {
      Component: lazy(
        () =>
          import("features/vnskySupportAlert/presentation/view/VnskySupportAlertFeature"),
      ),
      token: VnskySupportAlertToken.VnskySupportAlertViewModel,
    },
    StoreManagementFeature: {
      Component: lazy(
        () =>
          import("features/storeManagement/presentation/view/StoreManagementFeature"),
      ),
      token: StoreToken.StoreViewModel,
    },
    StaffManagementFeature: {
      Component: lazy(
        () =>
          import("features/b2cUserManagement/presentation/views/staffManagementFeature/StaffManagementFeature"),
      ),
      token: B2CUserToken.StaffManagementViewModel,
    },
    PackageManagementFeature: {
      Component: lazy(
        () =>
          import("features/packageManagement/views/PackageManagementFeature"),
      ),
      token: InjectionToken.PackageManagementViewModel,
      addtionalProps: {
        service: MobileDataService.BuyPackage,
        categoriesForQuery: "",
      },
    },
    PackageManagementForTravelSimFeature: {
      Component: lazy(
        () =>
          import("features/packageManagement/views/PackageManagementFeature"),
      ),
      token: InjectionToken.PackageManagementViewModel,
      addtionalProps: {
        service: MobileDataService.TravelSim,
        categoriesForQuery: MobileDataCategories.TravelSim,
      },
    },
    PackageManagementForSimEcommerceFeature: {
      Component: lazy(
        () =>
          import("features/packageManagement/views/PackageManagementFeature"),
      ),
      token: InjectionToken.PackageManagementViewModel,
      addtionalProps: {
        service: MobileDataService.SimEcommerce,
        categoriesForQuery: MobileDataCategories.SimEcommerce,
      },
    },
    DiscountManagementFeature: {
      Component: lazy(
        () =>
          import("features/discountManagement/presentation/view/DiscountManagementView"),
      ),
      token: DiscountManagementToken.DiscountManagementViewModel,
    },
    AppProfileRegisterFeature: {
      Component: lazy(
        () =>
          import("features/appProfileRegister/presentation/view/AppProfileRegisterView"),
      ),
      token: AppProfileToken.AppProfileViewModel,
    },
    IndividualUsersManagementFeature: {
      Component: lazy(
        () =>
          import("features/individualUsersManagement/presentation/views/IndividualUsersManagementView"),
      ),
      token: IndividualUserToken.IndividualUsersManagementViewModel,
    },
    AppFeedbackFeature: {
      Component: lazy(
        () =>
          import("features/appFeedback/presentation/views/AppFeedbackFeature"),
      ),
      token: AppFeedbackToken.AppFeedbackViewModel,
    },
    SystemUsersManagementFeature: {
      Component: lazy(
        () =>
          import("features/systemUserManagement/presentation/view/SystemUserView"),
      ),
      token: SystemUserToken.SystemUserViewModel,
    },
    B2CUserManagementFeature: {
      Component: lazy(
        () =>
          import("features/b2cUserManagement/presentation/views/B2CUserManagementFeature"),
      ),
    },
    PartnerManagementFeature: {
      Component: lazy(
        () =>
          import("features/partnerManagement/presentation/PartnerManagementFeature"),
      ),
    },
    SupplierManagementFeature: {
      Component: lazy(
        () =>
          import("features/supplierManagement/presentation/views/SupplierManagementFeature"),
      ),
      token: SupplierManagementToken.SupplierManagementViewModel,
    },
    B2BPackageManagementFeature: {
      Component: lazy(
        () =>
          import("features/packageManagement/b2bPackageManagement/presentation/view/B2BPackageManagementFeature"),
      ),
    },
    ProvinceConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/provinceConfiguration/views/ProvinceConfigurationFeature"),
      ),
    },
    WardConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/wardConfiguration/views/WardConfigurationFeature"),
      ),
    },
    DistrictConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/districtConfiguration/views/DistrictConfigurationFeature"),
      ),
    },
    CountryConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/countryConfiguration/presentation/views/CountryConfigurationFeature"),
      ),
      token: CountryConfigToken.CountryConfigViewModel,
    },
    CdnConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/cdnConfiguration/presentation/view/CdnConfigurationView"),
      ),
      token: CdnConfigToken.CdnConfigurationViewModel,
    },
    ManageConfigFeature: {
      Component: lazy(
        () => import("features/manageConfig/views/ManageConfigFeature"),
      ),
    },
    PermissionDataFeature: {
      Component: lazy(
        () =>
          import("features/permissionDataManagement/presentation/views/PermissionDataFeature"),
      ),
      token: PermissionDataToken.PermissionDataViewModel,
    },
    CapabilityFeature: {
      Component: lazy(
        () =>
          import("features/capabilityManagement/presentation/views/CapabilityFeature"),
      ),
      token: CapabilityToken.CapabilityViewModel,
    },
    UserPermissionFeature: {
      Component: lazy(
        () =>
          import("features/userPermissionManagement/presentation/views/UserPermissionFeature"),
      ),
      token: UserPermissionToken.UserPermissionViewModel,
    },
    RoleManagementFeature: {
      Component: lazy(
        () =>
          import("features/roleManagement/presentation/views/RoleManagementFeature"),
      ),
      token: RoleManagmentToken.RoleManagementViewModel,
    },
    IOTSimListLookupFeature: {
      Component: lazy(
        () =>
          import("features/iotSimListLookup/presentation/view/IOTSimListLookupView"),
      ),
      token: IOTSimListLookupToken.IOTSimListLookupViewModel,
    },
    ActionLogFeature: {
      Component: lazy(
        () =>
          import("features/actionLogManagement/presentation/views/ActionLogFeature"),
      ),
      token: ActionLogToken.ActionLogViewModel,
    },
  };

  static getPage(key: string): any {
    return this.PageKeys[key]?.Component || Notfound;
  }

  static getToken(key: string) {
    return this.PageKeys[key]?.token;
  }

  static getAddtionalProps(key: string) {
    return this.PageKeys[key]?.addtionalProps;
  }
}

export default AppPages;
